"use client";

import { FileSearch, Loader2, Search } from "lucide-react";
import { useParams } from "next/navigation";
import type { FormEvent } from "react";
import { useToolStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  WorkResultCard,
  WorkResultCardSkeleton,
  type WorkResultData,
} from "@/components/work-result-card";
import { formatCount, t } from "@/lib/i18n";

interface WorksSearchFormProps {
  query: string;
  onQueryChange: (query: string) => void;
  onSubmit: (e: FormEvent) => void;
  loading: boolean;
  results: WorkResultData[];
  totalResults: number;
  onSelectWork: (work: WorkResultData) => void;
  error: string | null;
}

export function WorksSearchForm({
  query,
  onQueryChange,
  onSubmit,
  loading,
  results,
  totalResults,
  onSelectWork,
  error,
}: WorksSearchFormProps) {
  const s = useToolStrings();
  const params = useParams<{ locale?: string }>();
  const locale = params?.locale ?? "en";
  return (
    <div className="space-y-4">
      <form className="space-y-2" onSubmit={onSubmit}>
        <Label htmlFor="works-search">{s.worksSearchLabel}</Label>
        <div className="flex gap-2">
          <Input
            className="flex-1"
            id="works-search"
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="e.g. Attention is all you need..."
            type="search"
            value={query}
          />
          <Button disabled={loading} type="submit">
            {loading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Search className="size-4" />
            )}
            {s.search}
          </Button>
        </div>
        <p className="text-muted-foreground text-xs">{s.worksSearchSource}</p>
      </form>

      {/* Loading Skeletons */}
      {loading && (
        <div className="space-y-3">
          {["skeleton-1", "skeleton-2", "skeleton-3"].map((id) => (
            <WorkResultCardSkeleton key={id} />
          ))}
        </div>
      )}

      {/* Work Results */}
      {!loading && results.length > 0 && (
        <div className="space-y-3">
          {totalResults > 0 && (
            <p className="text-muted-foreground text-sm">
              {t(s.showingResults, {
                shown: results.length,
                total: formatCount(locale, totalResults),
              })}
            </p>
          )}
          <div className="max-h-[450px] space-y-3 overflow-y-auto pe-1">
            {results.map((work) => (
              <WorkResultCard
                key={work.doi}
                onSelect={onSelectWork}
                work={work}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!(loading || error) && results.length === 0 && query && (
        <div className="py-8 text-center text-muted-foreground">
          <FileSearch className="mx-auto mb-3 size-12 opacity-40" />
          <p>{s.worksSearchEmpty}</p>
        </div>
      )}
    </div>
  );
}
